import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useBoardChartData } from '../../hooks/useBoardChartData';
import { BaseChartProps } from './types';
import { CHART_COLORS, CHART_STYLES, getCurrencySymbol, formatCompactNumber } from './chartTheme';
import { useSetChartMetadata } from './ChartContext';
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
} from 'recharts';

export const RevenuePerUnitChart: React.FC<BaseChartProps> = () => {
    const { t } = useTranslation();
    const { finance, entities, currencySettings } = useBoardChartData();

    const currencySymbol = useMemo(
        () => getCurrencySymbol(currencySettings?.default),
        [currencySettings?.default]
    );

    const chartData = useMemo(() => {
        if (!finance?.transactions) return [];

        const productData: Record<string, { name: string; total: number; count: number }> = {};

        for (const tx of finance.transactions) {
            if (['product_sale', 'sale'].includes(tx.category as string) && tx.type === 'REVENUE') {
                const id = tx.entityId as string;
                if (!productData[id]) {
                    const entity = entities?.find((e: any) => e.id === id);
                    productData[id] = {
                        name: (entity?.name as string) || id,
                        total: 0,
                        count: 0,
                    };
                }
                productData[id].total += Math.abs(Number(tx.amount) || 0);
                productData[id].count += 1;
            }
        }

        return Object.values(productData)
            .map((p) => ({
                name: p.name,
                avgRevenue: p.count > 0 ? Math.round((p.total / p.count) * 100) / 100 : 0,
            }))
            .sort((a, b) => b.avgRevenue - a.avgRevenue)
            .slice(0, 10);
    }, [finance?.transactions, entities]);

    const isEmpty = useMemo(() => chartData.length === 0, [chartData]);

    useSetChartMetadata({ isEmpty });

    return (
        <div className="w-full h-full bg-transparent overflow-hidden flex flex-col">
            <div className="flex-1 min-h-0 p-2">
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                        data={chartData}
                        margin={CHART_STYLES.margin.withAxis}
                    >
                        <CartesianGrid
                            vertical={false}
                            stroke={CHART_STYLES.grid.stroke}
                            strokeDasharray={CHART_STYLES.grid.strokeDasharray}
                            strokeWidth={CHART_STYLES.grid.strokeWidth}
                            opacity={CHART_STYLES.grid.opacity}
                        />
                        <XAxis
                            dataKey="name"
                            axisLine={CHART_STYLES.axis.axisLine}
                            tickLine={CHART_STYLES.axis.tickLine}
                            tick={CHART_STYLES.axis.tick}
                            tickMargin={CHART_STYLES.axis.tickMargin}
                            tickFormatter={(value: string) =>
                                value.length > 12 ? `${value.slice(0, 12)}...` : value
                            }
                        />
                        <YAxis
                            axisLine={CHART_STYLES.axis.axisLine}
                            tickLine={CHART_STYLES.axis.tickLine}
                            tick={CHART_STYLES.axis.tick}
                            tickFormatter={(value: number) => formatCompactNumber(value, currencySymbol)}
                        />
                        <Tooltip
                            contentStyle={CHART_STYLES.tooltip.contentStyle}
                            cursor={CHART_STYLES.tooltip.cursor}
                            formatter={(value: number) => [
                                formatCompactNumber(value, currencySymbol),
                                t('charts.avgRevenuePerUnit', 'Avg Revenue/Unit'),
                            ]}
                        />
                        <Bar
                            dataKey="avgRevenue"
                            fill={CHART_COLORS.secondary}
                            radius={CHART_STYLES.bar.radius}
                            maxBarSize={CHART_STYLES.bar.maxBarSize}
                        />
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};
